import { useEffect, useState } from "react";
import { api, formatErr, fmtMese, currentCompetenzaMese } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MonthPicker } from "@/components/MonthPicker";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { ExportPdfButton } from "@/components/ExportPdfButton";
import { Send } from "lucide-react";
import { toast } from "sonner";

export default function Invii() {
  const { company } = useCompany();
  const { user } = useAuth();
  const canSend = ["admin", "operatore_contabile"].includes(user?.role);
  const [mese, setMese] = useState(currentCompetenzaMese());
  const [padroncini, setPadroncini] = useState([]);
  const [sel, setSel] = useState([]);
  const [channels, setChannels] = useState(["email"]);
  const [log, setLog] = useState([]);
  const [sending, setSending] = useState(false);

  const loadLog = () => api.get("/invii", { params: { company, mese } }).then((r) => setLog(r.data));
  useEffect(() => { api.get("/padroncini", { params: { company } }).then((r) => setPadroncini(r.data.filter(p => p.is_active))); setSel([]); }, [company]);
  useEffect(() => { loadLog(); /* eslint-disable-line */ }, [company, mese]);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleCh = (c) => setChannels((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const invia = async () => {
    if (!sel.length || !channels.length) return toast.error("Seleziona padroncini e canale");
    if (!window.confirm(`Inviare il conteggio di ${fmtMese(mese)} a ${sel.length} padroncini?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/invii/batch", { mese, padroncino_ids: sel, channels });
      toast.success(`Inviati: ${data.inviati} · Errori: ${data.errori}`);
      loadLog();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setSending(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-heading font-black">Invio conteggi</h1>
        <div className="flex items-center gap-3">
          <Label className="text-xs font-heading uppercase">Mese</Label>
          <MonthPicker value={mese} onChange={setMese} testId="invii-mese" />
          <label className="flex items-center gap-1.5 text-sm"><Checkbox checked={channels.includes("email")} onCheckedChange={() => toggleCh("email")} data-testid="invii-ch-email" />Email</label>
          <label className="flex items-center gap-1.5 text-sm"><Checkbox checked={channels.includes("whatsapp")} onCheckedChange={() => toggleCh("whatsapp")} data-testid="invii-ch-whatsapp" />WhatsApp</label>
          {canSend && <Button size="sm" className="h-8 gap-1" onClick={invia} disabled={sending} data-testid="invii-send"><Send className="w-4 h-4" />{sending ? "Invio…" : `Invia (${sel.length})`}</Button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="rounded-md overflow-hidden">
          <table className="autotrans-table w-full">
            <thead><tr>
              <th className="w-8"><Checkbox checked={sel.length > 0 && sel.length === padroncini.length}
                onCheckedChange={(v) => setSel(v ? padroncini.map(p => p.id) : [])} data-testid="invii-select-all" /></th>
              <th>Padroncino</th><th>Email</th><th>WhatsApp</th></tr></thead>
            <tbody>
              {padroncini.map((p) => (
                <tr key={p.id}>
                  <td><Checkbox checked={sel.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`invii-sel-${p.id}`} /></td>
                  <td>{p.codice} · {p.nome} {p.cognome}</td>
                  <td className="text-xs">{p.email || <span className="text-destructive">mancante</span>}</td>
                  <td className="text-xs">{p.whatsapp || p.telefono || <span className="text-destructive">mancante</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="rounded-md overflow-hidden">
          <div className="p-3 border-b font-heading font-bold flex items-center justify-between">Log invii · {fmtMese(mese)}
            <ExportPdfButton resource="invii" params={{ company, mese }} /></div>
          <table className="autotrans-table w-full" data-testid="invii-log">
            <thead><tr><th>Quando</th><th>Padroncino</th><th>Canale</th><th>Stato</th></tr></thead>
            <tbody>
              {log.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">Nessun invio</td></tr>}
              {log.map((l) => (
                <tr key={l.id}>
                  <td className="text-xs font-mono">{new Date(l.created_at).toLocaleString("it-IT")}</td>
                  <td>{l.padroncino_nome}</td>
                  <td><Badge variant="outline" className="h-5">{l.canale}</Badge></td>
                  <td>{l.stato === "inviato" ? <Badge className="bg-green-600 h-5">Inviato</Badge> :
                    <Badge className="bg-destructive h-5" title={l.errore}>Errore</Badge>}
                    {l.errore && <div className="text-xs text-muted-foreground">{l.errore}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
